/**
 * Per-dispatch helpers for the expanded dispatch view (P2-1306 / P2-1304).
 *
 * Mirrors the web app's DispatchTimeline/utils.ts so the plugin's dispatch
 * view matches the web: main-only numbering, empty-move bundling, and
 * per-dispatch miles. Kept intentionally close to the web implementation —
 * see Bridge18-v2/src/components/_dashboard/loadnew/DispatchTimeline/utils.ts.
 */

import { LoadDispatch, LoadStop, LoadDetails } from '@/types/load'
import { isEmptyMoveStop, getAllStopsSorted } from './stopUtils'

const METERS_PER_MILE = 1609.344

/** Convert meters to miles. Returns 0 for invalid input. */
export const metersToMiles = (meters: number | undefined | null): number => {
  if (typeof meters !== 'number' || !isFinite(meters) || meters <= 0) return 0
  return meters / METERS_PER_MILE
}

/**
 * Format a mile value for display.
 *   - Under 1 mi → "0.xx mi"
 *   - 1 to 10    → "x.x mi"
 *   - 10+        → "N mi"
 * The thresholds compare the ROUNDED value, so 9.99 reads "10 mi" (never
 * "10.0 mi") and 0.996 reads "1.0 mi" (never "1.00 mi").
 */
export const formatMiles = (miles: number): string => {
  if (!isFinite(miles) || miles <= 0) return '0 mi'
  if (Math.round(miles * 100) < 100) return `${miles.toFixed(2)} mi`
  if (Math.round(miles * 10) < 100) return `${miles.toFixed(1)} mi`
  return `${Math.round(miles)} mi`
}

/**
 * Sum within-segment distance in meters. Prefers each stop's per-dispatch
 * leg (dispatch_ets_details — direct point-to-point to the next stop WITHIN
 * the dispatch, P2-1300), falling back to the overall-route ets_details for
 * empty-move stops (BE skips them) and loads not yet backfilled. Sums
 * stops[0..N-2] — the last stop's leg points outside this segment.
 */
export const getDispatchMeters = (stops: LoadStop[]): number => {
  if (!Array.isArray(stops) || stops.length < 2) return 0
  let total = 0
  for (let i = 0; i < stops.length - 1; i++) {
    const leg = stops[i]?.dispatch_ets_details ?? stops[i]?.ets_details
    // distanceValue may arrive as a numeric string from the API.
    const dv = Number(leg?.distanceValue)
    if (isFinite(dv) && dv > 0) total += dv
  }
  return total
}

/**
 * Resolve a dispatch's stops[] (string id refs) to actual stop objects from
 * the merged sortedStops, re-ordered to the sortedStops sequence (the
 * persisted dispatch.stops order is not reliable). Unresolved ids drop out.
 */
export const resolveDispatchStops = (
  dispatch: LoadDispatch | null | undefined,
  sortedStops: LoadStop[]
): LoadStop[] => {
  if (!dispatch?.stops?.length || !sortedStops?.length) return []
  const stopMap = new Map<string, LoadStop>(
    sortedStops.map(s => [s?._id || '', s])
  )
  const positionById = new Map<string, number>(
    sortedStops.map((s, i) => [s?._id || '', i])
  )
  return (dispatch.stops as unknown[])
    .map(ref => {
      const id = typeof ref === 'string' ? ref : (ref as LoadStop)?._id
      const found = id ? stopMap.get(id) : undefined
      if (found) return found
      if (ref && typeof ref === 'object' && (ref as LoadStop)?._id)
        return ref as LoadStop
      return null
    })
    .filter((s): s is LoadStop => Boolean(s))
    .sort(
      (a, b) =>
        (positionById.get(a?._id || '') ?? Infinity) -
        (positionById.get(b?._id || '') ?? Infinity)
    )
}

/** Filter out empty-move-only dispatches → just the "main" dispatches. */
export const getMainDispatches = (
  dispatches: LoadDispatch[] | undefined
): LoadDispatch[] => {
  if (!Array.isArray(dispatches)) return []
  return dispatches.filter(d => !d?.isEmptyMoveDispatch)
}

/**
 * Find the empty-move sibling for a main dispatch
 * (linkedMainDispatchId === main.dispatchId), or null.
 */
export const findEmptyMoveSibling = (
  mainDispatch: LoadDispatch,
  allDispatches: LoadDispatch[]
): LoadDispatch | null => {
  if (!mainDispatch?.dispatchId || !allDispatches?.length) return null
  return (
    allDispatches.find(
      d =>
        d?.isEmptyMoveDispatch &&
        d?.linkedMainDispatchId === mainDispatch.dispatchId
    ) || null
  )
}

/**
 * Same-driver continuation. Trusts the BE-frozen
 * isContinuationOfPreviousDriver flag only when it doesn't contradict the
 * current driver data (the flag is frozen at creation and goes stale when
 * the driver is later removed/replaced — same guard as the web). Falls
 * back to comparing driverDetails._id when the flag is absent.
 */
export const isContinuationDispatch = (
  current: LoadDispatch,
  previous: LoadDispatch | undefined
): boolean => {
  if (!previous) return false
  const currId = current?.driverDetails?._id
  const prevId = previous?.driverDetails?._id
  if (current?.isContinuationOfPreviousDriver === true) {
    if (!currId) return false
    if (prevId && currId !== prevId) return false
    return true
  }
  return !!currId && !!prevId && currId === prevId
}

/**
 * The dispatch's OWN start stop — its first real (non-empty-move) stop.
 *
 * This is the per-dispatch "Start Point" shown in the expanded card. It
 * mirrors the web exactly: DashboardDispatch renders a main dispatch's start
 * as `getLoadStopFacilityDetails(dispatch.stops[0])` and buildRowStops takes
 * `mainStops[0]` after filtering empty-move stops (P2-1304). The empty-move
 * ORIGIN (where the driver started before the empty move) is NOT shown here —
 * it's conveyed separately by the Empty Move miles. EM stops are excluded so
 * the start point is the dispatch's own first pickup/stop, never the EM
 * sibling's origin or a previous dispatch's location.
 *
 * Returns null when the dispatch has no resolvable non-EM stop (caller then
 * omits the Start Point rather than showing a stale value).
 */
export const getDispatchStartStop = (
  dispatch: LoadDispatch | null | undefined,
  sortedStops: LoadStop[]
): LoadStop | null => {
  const stops = resolveDispatchStops(dispatch, sortedStops).filter(
    s => !isEmptyMoveStop(s)
  )
  return stops[0] || null
}

/**
 * Compose one dispatch's stops with its empty move prepended — a direct port
 * of the web's buildRowStops (DispatchTimeline/utils.ts), the "expanded row"
 * the plugin mirrors. Strips any EM-flagged stop that leaked into main.stops,
 * then for a non-continuation dispatch prepends the EM sibling's stop(s).
 * Continuation dispatches (same driver as previous) get no EM prepend.
 */
export const buildRowStops = (
  main: LoadDispatch,
  emSibling: LoadDispatch | null,
  sortedStops: LoadStop[],
  isContinuation: boolean
): LoadStop[] => {
  const mainStops = resolveDispatchStops(main, sortedStops).filter(
    s => !isEmptyMoveStop(s)
  )
  if (mainStops.length === 0) return []
  if (isContinuation || !emSibling) return mainStops
  // P2-1626: an EM dispatch persists its stops EM-first ([emStop, firstPickup]),
  // but resolveDispatchStops re-sorts them by position in `sortedStops` — and an
  // EM stop's `index` is its slot in load.emptyMoveStops, an EM-creation ordinal,
  // NOT a route position (the backend appends and renumbers that array on every
  // EM re-cut, see packages/loadops/.../reconcileEmptyMove.ts). Whenever that
  // ordinal exceeds its destination's route index the EM sorted BEHIND its own
  // pickup, so the bundle came back as [pickup, EM] and the empty-stop icon
  // rendered mid-segment instead of at the start of the empty leg.
  //
  // This repo is additionally exposed because getAllStopsSorted (stopUtils.ts)
  // has no EM-first tie-break at all, so even the equal-index case is left to
  // sort stability. Re-assert the invariant structurally: an EM leads its bundle.
  // Mirrors the web fix in Bridge18-v2 DispatchTimeline/utils.ts.
  const emResolved = resolveDispatchStops(emSibling, sortedStops)
  const emStops = [
    ...emResolved.filter(s => isEmptyMoveStop(s)),
    ...emResolved.filter(s => !isEmptyMoveStop(s))
  ]
  const seen = new Set<string>()
  return [...emStops, ...mainStops].filter(s => {
    const id = s?._id || ''
    if (!id || seen.has(id)) return false
    seen.add(id)
    return true
  })
}

/**
 * The full route stop list in DISPATCH order, each dispatch's empty move
 * bundled at the head of its OWN segment — mirroring the web's expanded
 * dispatch rows (the reference for P2-1315).
 *
 * Empty-move stops carry placeholder indices in the data (EM-1 and EM-2 both
 * sort to the front), so a plain index sort (getAllStopsSorted) mislays a
 * second driver's EM-2 as the 2nd stop overall. Composing per dispatch instead
 * positions each EM right before its own segment's first pickup. Main
 * dispatches are walked in route order (by their first own-stop's position);
 * any stop not owned by a dispatch (unassigned) is appended in index order so
 * nothing is dropped. Falls back to the plain index sort when the load has no
 * main dispatches.
 */
export const getOrderedRouteStops = (
  load: LoadDetails | null | undefined
): LoadStop[] => {
  const sortedStops = getAllStopsSorted(load?.stops, load?.emptyMoveStops)
  const dispatches = load?.dispatches || []
  const mains = getMainDispatches(dispatches)
  if (!mains.length) return sortedStops

  // Walk main dispatches in route order — by the sortedStops position of each
  // dispatch's first real (non-EM) stop. Dispatch persistence order isn't
  // reliable, and EM stops sit at the front, so order off the first pickup.
  const positionOf = (d: LoadDispatch): number => {
    const first = resolveDispatchStops(d, sortedStops).filter(
      s => !isEmptyMoveStop(s)
    )[0]
    const id = first?._id
    const idx = id ? sortedStops.findIndex(s => s?._id === id) : -1
    return idx === -1 ? Infinity : idx
  }
  const orderedMains = [...mains].sort((a, b) => positionOf(a) - positionOf(b))

  const out: LoadStop[] = []
  const seen = new Set<string>()
  const push = (s: LoadStop) => {
    const id = s?._id || ''
    if (!id || seen.has(id)) return
    seen.add(id)
    out.push(s)
  }

  orderedMains.forEach((main, idx) => {
    const prev = idx > 0 ? orderedMains[idx - 1] : undefined
    const isContinuation = isContinuationDispatch(main, prev)
    const emSibling = findEmptyMoveSibling(main, dispatches)

    // P2-1665: buildRowStops deliberately drops the EM for a same-driver
    // continuation, and that is right for the dispatch CARD — no empty leg is
    // driven, so it must not be bundled into that row or its miles. But this
    // is the flat route list, and a dropped EM stop then fell through to the
    // unassigned sweep below, which appends in `index` order. An EM stop's
    // index is its slot in load.emptyMoveStops (an EM-creation ordinal, the
    // same namespace confusion behind P2-1626), not a route position, so
    // EM-6 landed after Drop #4 — four days out of sequence.
    //
    // Position it here instead, at the head of its own main dispatch's
    // segment, continuation or not. Only the ORDER of this list changes:
    // getOrderedRouteStops has exactly one consumer (the Stops list), and the
    // dispatch card calls buildRowStops/getDispatchMileage directly, so the
    // card's bundling and mileage are untouched.
    if (isContinuation && emSibling) {
      resolveDispatchStops(emSibling, sortedStops)
        .filter(s => isEmptyMoveStop(s))
        .forEach(push)
    }

    buildRowStops(
      main,
      isContinuation ? null : emSibling,
      sortedStops,
      isContinuation
    ).forEach(push)
  })

  // Unassigned stops (not owned by any dispatch) — keep them, in index order.
  sortedStops.forEach(push)
  return out
}

/**
 * Per-dispatch miles, split into the empty-move leg and the dispatch's own
 * route. emMeters is derived as (bundled − route) so it captures the
 * EM-origin → first-pickup bridge segment, which getDispatchMeters over the
 * EM stops alone would exclude.
 */
export const getDispatchMileage = (
  main: LoadDispatch,
  emSibling: LoadDispatch | null,
  sortedStops: LoadStop[],
  isContinuation: boolean
): { emMiles: number; routeMiles: number; totalMiles: number } => {
  // Same strip as buildRowStops, so a leaked EM stop's leg stays in emMiles.
  // An EM dispatch passed as main (no-main fallback) keeps its own EM stops.
  const resolvedMain = resolveDispatchStops(main, sortedStops)
  const mainStops = main?.isEmptyMoveDispatch
    ? resolvedMain
    : resolvedMain.filter(s => !isEmptyMoveStop(s))
  const emStops =
    !isContinuation && emSibling
      ? resolveDispatchStops(emSibling, sortedStops)
      : []

  const seen = new Set<string>()
  const bundled = [...emStops, ...mainStops].filter(s => {
    const id = s?._id || ''
    if (!id || seen.has(id)) return false
    seen.add(id)
    return true
  })

  const routeMeters = getDispatchMeters(mainStops)
  const bundledMeters = getDispatchMeters(bundled)
  const emMeters = Math.max(bundledMeters - routeMeters, 0)

  return {
    emMiles: metersToMiles(emMeters),
    routeMiles: metersToMiles(routeMeters),
    totalMiles: metersToMiles(bundledMeters)
  }
}
